import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, inject, Input, OnChanges, OnInit, PLATFORM_ID, SimpleChanges, ViewChild } from '@angular/core';
import { Property, PropertyImage } from '../../models/property.model';
import { User } from '../../../../shared/user/models/user.model';
import { filter, Subscription, tap } from 'rxjs';
import { AuthService } from '../../../../core/services/auth.service';
import { PropertyLikeService } from '../../services/property-like.service';
import { DomSanitizer, Meta, SafeHtml, Title } from '@angular/platform-browser';
import { Location } from '../../../../shared/general/models/location.model';
import { environment } from '../../../../../environments/environment';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule, isPlatformBrowser, TitleCasePipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { GoogleMapComponent } from '../../../../shared/general/components/google-map/google-map';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { PropertyCalendarComponent } from '../property-calendar/property-calendar';
import { PropertyService } from '../../services/property.service';
import { PropertyAmenityView } from '../property-amenity-view/property-amenity-view';
import { GtmService } from '../../../../core/services/gtm.service';
import { BookingRequestButtonComponent } from "../../../../shared/general/components/booking/booking-request-button/booking-request-button";
import { PropertyCardFooterComponent } from '../property-card-footer/property-card-footer';
import { SeoService } from '../../../../shared/services/seo.service';
import { ShareButtonComponent } from "../../../../shared/general/components/share-button/share-button";
import { PropertyGalleryComponent } from '../property-gallery/property-gallery';
import { SaveButtonComponent } from '../save-button/save-button';
import { AmenityService } from '../../services/amenity.service';

@Component({
  selector: 'app-property-card',
  providers: [TitleCasePipe],
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    GoogleMapComponent,
    MatChipsModule,
    MatButtonModule,
    MatDividerModule,
    PropertyCalendarComponent,
    PropertyAmenityView,
    BookingRequestButtonComponent,
    PropertyCardFooterComponent,
    ShareButtonComponent,
    PropertyGalleryComponent,
    SaveButtonComponent
  ],
  templateUrl: './property-card.html',
  styleUrl: './property-card.scss',
})
export class PropertyCard implements OnInit, OnChanges, AfterViewInit {
  private authService = inject(AuthService);
  private propertyService = inject(PropertyService);
  private likeService = inject(PropertyLikeService);
  private sanitizer = inject(DomSanitizer);
  private cdr = inject(ChangeDetectorRef);
  private gtm = inject(GtmService);
  private seo = inject(SeoService);
  private platformId = inject(PLATFORM_ID);

  private meta = inject(Meta);
  private title = inject(Title);
  @Input() property: Property | undefined;
  @ViewChild('calendarSection') calendarSection!: ElementRef;
  private titleCasePipe = inject(TitleCasePipe);
  mainImg: PropertyImage | undefined;
  user: User | null = null;
  username: string | null = null;
  isLoading = true;
  locations: Location[] = [];
  private userSubscription: Subscription | undefined;
  private amenityService = inject(AmenityService);
  private isUpdating = false;
  isBrowser = false;
  categories: string[] = ["Property Style", "Community Features", "Essential", "Comfort and Convenience", "Outdoor",
    "Waterfront and Boating", "Luxury and Recreation", "Family and Pet-Friendly",
    "Location-Specific", "Eco-Friendly and Smart", "Unique or Local", "Accessibility", "Safety and Practical"];
  mapHeight = '400px';

  ngOnInit() {
    this.isBrowser = isPlatformBrowser(this.platformId);

    this.mainImg = this.property?.images.find(img => img.sortOrder === 0) ?? this.property?.images[0];

    this.userSubscription = this.authService.user$.pipe(
      filter((user) => user !== undefined),
      tap((user) => {
        this.isLoading = false;
        if (user) {
          this.user = user;
          this.username = user.username;
          this.cdr.detectChanges();
        }
      })
    ).subscribe();

    this.updateLocations();
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => this.calculateMapHeight(), 0);
      // Force Angular to check the new mapHeight value to prevent NG0100
      this.cdr.detectChanges(); 
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['property'] && changes['property'].currentValue) {
      this.property = changes['property'].currentValue;
      this.mainImg = this.property?.images[0] ?? undefined;
      this.updateLocations();
      this.cdr.detectChanges();
    }
  }

  onThumbnailClick(image: PropertyImage | string): void {
    this.mainImg = typeof image === 'string' ? undefined : image;
  }

  updateLocations() {
    if (this.isUpdating) return; 
    this.isUpdating = true;

    if (this.property?.address?.lat && this.property?.address?.lng) {
      this.locations = [{
        lat: this.property.address.lat,
        lng: this.property.address.lng,
        name: `${this.property.displayName ?? this.property.propertyType} in ${this.property.address.city}, ${this.property.address.state}`,
        imageUrl: this.property.images[0].imageUrl ?? '',
        liked: this.property.liked
      }];
    }
    this.isUpdating = false;
    this.cdr.detectChanges();
  }

  trackByFn(index: number, item: any) {
    return item.id || index; // Adjust based on your data
  }

  amenitiesByCategory = (category: string) => {
    if (this.property !== null) {
      return this.property?.amenities?.filter(p => p.category == category);
    } else {
      return [];
    }
  }

  getSafeSizeHtml(): SafeHtml {
    const size = this.property?.sizeSqft;
    const html = size ? `${size} ft<sup>2</sup>` : 'N/A';
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  getCategoryIcon(category: string) {
    return this.amenityService.getCategoryIcon(category);
  
  }

  getPropertyTitle() {
    const type = this.property?.propertyType || 'Property';
    return `${this.property?.displayName ?? this.titleCasePipe.transform(type)} in ${this.property?.address.city}, ${this.property?.address.state} on Horizinns`;
  }

  getPropertyUrl() {
    return `${environment.appUrl}/property/${this.property?.id}`;
  }

  @HostListener('window:resize')
  onResize() {
    if (isPlatformBrowser(this.platformId)) {
      this.calculateMapHeight();
      this.cdr.detectChanges();
    }
  }

  calculateMapHeight() {
    if (!isPlatformBrowser(this.platformId)) {
      this.mapHeight = '400px';
      this.cdr.detectChanges();
      return;
    }

    if (this.calendarSection) {
      const height = this.calendarSection.nativeElement.offsetHeight;
      if (window.innerWidth < 768) {
        this.mapHeight = '300px';
      } else {
        this.mapHeight = height > 0 ? `${height}px` : '400px';
      }
      this.cdr.detectChanges();
    }
  }

  getMapHeight(): string {
    return this.mapHeight;
  }
}
